import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Panel, Stat, Tag, Icon, EmptyState, StatusDot,
  DataTable, useErrorDialog, type Column,
} from '@dayon/design-system';
import { formatPesos, formatPesosCompact, formatCount } from '@dayon/money';
import type { LicenceWarning } from '@dayon/contracts';
import { gamingTime } from '@dayon/contracts';
import type { Permission } from '@dayon/rbac';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { formatWait } from '../../lib/wait.ts';
import { LicenceWarnings } from './LicenceWarnings.tsx';
import { WORK_KINDS, queueOpens, rowsWithheld, type WorkKind } from './queue-links.ts';

export interface ActionItem {
  /** The item's reference — for a KYC review row, the player's: a check has none of its own. */
  item: string;
  kind: WorkKind;
  player: string | null;
  brand: string | null;
  amount: string | null;
  targetId: string;
  waitingMinutes: number;
}

interface LivePayload {
  live: {
    depositsLastHour: string;
    depositCountLastHour: number;
    withdrawalsPending: string;
    withdrawalCountPending: number;
    oldestPendingMinutes: number | null;
    ggrToday: string;
    roundsToday: number;
    activeToday: number;
    holdTodayBp: number | null;
  };
  queue: {
    withdrawalsAwaitingChecker: { count: number; value: string; oldestMinutes: number | null; breachingSla: number };
    kycReview: { count: number; oldestMinutes: number | null };
    riskAlerts: { count: number; critical: number };
    supportTickets: { count: number; escalated: number };
  };
  actionList: ActionItem[];
  /* How many items there ACTUALLY are, which is not `actionList.length`: the
     route returns the oldest 25. The header prints both when they differ, so a
     capped list never reads as a complete one. */
  actionListTotal: number;
  rails: { name: string; provider: string; status: string; kind: string }[];
  providers: { name: string; code: string; status: string }[];
  /* The 60-day licence warning, on the same terms as the Split view. `/`
     remembers which view you last used, so a warning drawn on one of them and
     not the other is a warning half the operators never meet. */
  licences: LicenceWarning[];
  unavailable: { section: string; reason: string }[];
}

const KIND_LABEL: Record<ActionItem['kind'], string> = {
  WITHDRAWAL: 'Withdrawal',
  KYC: 'KYC review',
  RISK: 'Risk alert',
  TICKET: 'Support',
};

/**
 * THE WORK LIST: one list across the four queues, oldest first.
 *
 * Its own component, with no hooks, so a test can draw it for every role and call what a row would
 * do.
 *
 * A ROW OPENS THE SCREEN ITS QUEUE LIVES ON, and only for a reader who holds the key that screen's
 * sidebar entry needs (queue-links.ts reads it from the sidebar's own table). For anyone else the
 * row is drawn as text rather than as a control — every cell still there — and one line under the
 * list, for each kind of row it holds, names that kind's key: a row that opened a refusal would be a
 * dead end. In today's table a Risk alert row needs players.restrict, which the Finance Lead, the
 * Auditor and the Support Agent lack, and a Withdrawal row transactions.view, which the Support
 * Agent lacks.
 *
 * A KYC review row is an identity check waiting for a person and opens the identity queue's waiting
 * list. Until 23 October 2026 these rows were the older process's PENDING submissions and opened the
 * roster filtered to Pending.
 */
export function WorkList({
  items,
  allowed,
  role,
  onOpen,
  empty,
}: {
  items: ActionItem[];
  allowed: (key: Permission) => boolean;
  /** The reader's role, named in the line that stands in for a link. */
  role: string;
  onOpen: (to: string) => void;
  empty: ReactNode;
}) {
  const columns: Column<ActionItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (r) => (
        <Tag tone={r.kind === 'RISK' ? 'outline' : r.kind === 'WITHDRAWAL' ? 'accent' : 'neutral'}>
          {r.item}
        </Tag>
      ),
    },
    { key: 'kind', header: 'Queue', render: (r) => KIND_LABEL[r.kind] },
    { key: 'player', header: 'Player', render: (r) => r.player ?? '—' },
    {
      key: 'brand',
      header: 'Brand',
      secondary: true,
      render: (r) => <span className="text-muted">{r.brand ?? '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (r) => (r.amount ? formatPesos(BigInt(r.amount)) : <span className="text-subtle">—</span>),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      numeric: true,
      // Two hours is the withdrawal SLA; anything past it is the thing to work. Printed as a wait,
      // as the tile above it is: oldest first, a raw count read "56379m" under "oldest 39d 3h".
      render: (r) => (
        <span className={r.waitingMinutes > 120 ? 'tabular stat-delta up' : 'tabular'}>
          {formatWait(r.waitingMinutes)}
        </span>
      ),
    },
  ];
  /* The kinds of row on the list this reader cannot open, in the band's order — one line each below. */
  const withheld = WORK_KINDS.filter(
    (kind) => items.some((r) => r.kind === kind) && queueOpens(kind, allowed) === null,
  );

  return (
    <>
      <DataTable
        caption="Open work across withdrawals, KYC review, risk alerts and support tickets, oldest first"
        columns={columns}
        rows={items}
        rowKey={(r) => `${r.kind}-${r.targetId}`}
        onRowClick={(r) => {
          const to = queueOpens(r.kind, allowed);
          if (to !== null) onOpen(to);
        }}
        rowClickable={(r) => queueOpens(r.kind, allowed) !== null}
        empty={empty}
      />
      {withheld.map((kind) => (
        <p key={kind} className="text-subtle work-list-note">
          {rowsWithheld(kind, KIND_LABEL[kind], role)}
        </p>
      ))}
    </>
  );
}

/**
 * Dashboard A — Live Ops.
 *
 * The design frames this as "updating every 5s", and the point of the screen is
 * the single work list: one queue across withdrawals, KYC, risk and tickets,
 * ordered oldest-first, because an operator works one list rather than four
 * screens.
 */
export function LiveOps() {
  const { brandId, allowed, session } = useSession();
  const { showError } = useErrorDialog();
  const navigate = useNavigate();
  const [data, setData] = useState<LivePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  /*
   * Whether the last read FAILED, as distinct from having returned nothing.
   *
   * This screen makes the strongest freshness claim in the product — a pulsing
   * LIVE pill, "updating every 5s", and a work list whose empty state reads
   * "all four queues are clear · this is read live, not cached". Every one of
   * those is a claim about NOW, and every one of them is false the moment a
   * read does not come back. Stopping the timestamp is too quiet a signal to
   * carry that on its own: an operator who glances at an empty list under a
   * LIVE pill goes home. So a failure is stated, in words, wherever the screen
   * would otherwise assert liveness.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  /* Only the newest read may set state. Polls overlap a brand change, and one
     that was already in flight lands after the read for the new scope — it
     would stamp the old brand's figures, or the old brand's failure, onto the
     new one. */
  const generation = useRef(0);

  const load = useCallback(
    async (silent: boolean) => {
      const mine = ++generation.current;
      if (!silent) setLoading(true);
      try {
        const query = new URLSearchParams();
        if (brandId) query.set('brandId', brandId);
        const d = await api.get<LivePayload>(`/api/dashboard/live?${query}`);
        if (mine !== generation.current) return;
        setData(d);
        setLoadFailed(false);
        setRefreshedAt(gamingTime(new Date()));
      } catch (error) {
        if (mine !== generation.current) return;
        // A failed BACKGROUND poll must not throw a modal over a screen the
        // operator is reading, so the last good figures stay — but they are
        // now MARKED as the last good figures rather than passed off as this
        // second's. A foreground read has no last-good to keep: whatever is on
        // screen belongs to the scope the operator just left, so it goes.
        setLoadFailed(true);
        if (!silent) {
          setData(null);
          showError({
            title: 'Could not load live operations',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        }
      } finally {
        if (!silent && mine === generation.current) setLoading(false);
      }
    },
    [brandId, showError],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // The design's 5s cadence. The action-queue counts are live; the KPI band
  // would be cached for 60s in production, but at this data volume one query
  // serves both.
  useEffect(() => {
    const timer = setInterval(() => void load(true), 5000);
    return () => clearInterval(timer);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="stat-grid">
        {Array.from({ length: 4 }, (_, i) => (
          <div className="stat" key={i}>
            <div className="skeleton" style={{ height: '60px' }}>&nbsp;</div>
          </div>
        ))}
      </div>
    );
  }
  /*
   * Nothing was read, and nothing is left to keep. The modal has been dismissed
   * by now, so this is the whole of what the operator has: it says the read
   * failed rather than showing an empty, calm screen.
   */
  if (!data) {
    if (!loadFailed) return null;
    return (
      <Panel>
        <EmptyState icon="warning-circle" title="Live operations could not be read">
          Live operations could not be fetched, so no queue, no rails and no provider statuses
          are drawn. Nothing here is clear — it is unknown. The screen keeps
          re-reading itself every 5 seconds and fills in as soon as one comes back.
        </EmptyState>
      </Panel>
    );
  }

  const { live, queue, actionList, actionListTotal, rails, providers, unavailable } = data;
  /* Older builds of the route did not send a total. Falling back to the row
     count keeps the panel rendering, and it is the honest fallback: with no
     total to compare against there is no claim to make about what is hidden. */
  const totalItems = actionListTotal ?? actionList.length;
  const hidden = totalItems - actionList.length;

  return (
    <>
      <div className="row">
        <div>
          <h3 style={{ margin: 0 }}>Live operations</h3>
          <div className="text-subtle" style={{ fontSize: '12.5px' }}>
            {new Date().toLocaleDateString('en-PH', { weekday: 'short', day: 'numeric', month: 'short' })}
            {' · '}
            {loadFailed
              ? `refresh failed · figures as of ${refreshedAt ?? 'the last read'} · retrying every 5s`
              : `${refreshedAt ? `updated ${refreshedAt}` : 'updating'} · every 5s`}
          </div>
        </div>
        {/* The pill is a claim, so it changes when the claim stops holding. */}
        <span className="tag tag-outline">
          {loadFailed ? (
            <>
              <StatusDot tone="quiet" label="Stale" />
              STALE
            </>
          ) : (
            <>
              <StatusDot tone="attention" live label="Live" />
              LIVE
            </>
          )}
        </span>
      </div>

      {/* Above the tiles, as on the Split view. A licence obligation is not an
          operational figure and does not wait for the next 5-second poll to be
          worth reading. */}
      <LicenceWarnings licences={data.licences} canOpenSettings={allowed('platform.view')} />

      {loadFailed && (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>These figures are not live.</strong> The last refresh failed, so every tile
            and list below is as it stood at {refreshedAt ?? 'the last successful read'} and has not
            moved since. Retrying
            every 5 seconds — the stamp above resumes when one comes back.
          </div>
        </div>
      )}

      <div className="stat-grid">
        <Stat
          label="Deposits · last hour"
          value={formatPesosCompact(BigInt(live.depositsLastHour))}
          delta={`${formatCount(live.depositCountLastHour)} transactions`}
        />
        <Stat
          label="Withdrawals · pending"
          value={formatPesosCompact(BigInt(live.withdrawalsPending))}
          delta={
            live.oldestPendingMinutes === null
              ? 'queue is clear'
              : `${live.withdrawalCountPending} awaiting checker · oldest ${formatWait(live.oldestPendingMinutes)}`
          }
          deltaUp={(live.oldestPendingMinutes ?? 0) > 120}
        />
        <Stat
          label="GGR today"
          value={formatPesosCompact(BigInt(live.ggrToday))}
          delta={`${live.holdTodayBp === null ? 'no turnover' : `Hold ${(live.holdTodayBp / 100).toFixed(1)}%`} · ${formatCount(live.roundsToday)} rounds`}
        />
        <Stat
          label="Players today"
          value={formatCount(live.activeToday)}
          delta="distinct players with a settled round"
        />
      </div>

      <Panel
        title="Needs action now"
        actions={
          <span className="text-subtle" style={{ fontSize: '11.5px' }}>
            {hidden > 0 ? (
              <>
                oldest {actionList.length} of {totalItems} items across 4 queues
              </>
            ) : (
              <>{totalItems} items across 4 queues</>
            )}{' '}
            · {queue.withdrawalsAwaitingChecker.breachingSla} breaching SLA
          </span>
        }
      >
        <WorkList
          items={actionList}
          allowed={allowed}
          role={session?.role ?? 'your role'}
          onOpen={(to) => navigate(to)}
          empty={
            loadFailed ? (
              <EmptyState icon="warning-circle" title="This list is not current">
                The last refresh failed, so this is the work list as it stood at{' '}
                {refreshedAt ?? 'the last successful read'}. Empty here means
                the four queues were clear THEN — not that nothing is waiting now.
              </EmptyState>
            ) : (
              <EmptyState icon="check-circle" title="Nothing is waiting">
                All four queues are clear in this brand scope. This is read live, not cached.
              </EmptyState>
            )
          }
        />
      </Panel>

      <div className="halves">
        <Panel title="Payment rails">
          <div className="stack">
            {rails.map((r) => (
              <div className="row" key={r.name}>
                <StatusDot
                  tone={r.status === 'LIVE' ? 'ok' : r.status === 'DEGRADED' ? 'attention' : 'quiet'}
                  label={r.status.toLowerCase()}
                />
                <span>{r.name}</span>
                <span className="text-subtle" style={{ fontSize: '12px' }}>
                  {r.provider}
                </span>
                <span className="header-spacer">
                  <Tag tone={r.status === 'DEGRADED' ? 'outline' : 'quiet'}>
                    {r.status.toLowerCase()}
                  </Tag>
                </span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Game providers">
          <div className="stack">
            {providers.map((p) => (
              <div className="row" key={p.code}>
                <StatusDot
                  tone={p.status === 'LIVE' ? 'ok' : p.status === 'MAINTENANCE' ? 'attention' : 'quiet'}
                  label={p.status.toLowerCase()}
                />
                <span>{p.name}</span>
                <span className="header-spacer">
                  <Tag tone={p.status === 'MAINTENANCE' ? 'outline' : 'quiet'}>
                    {p.status.toLowerCase()}
                  </Tag>
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Named gaps, never a fabricated figure. */}
      <Panel title="Not yet available">
        <div className="stack">
          {unavailable.map((u) => (
            <div className="notice notice-quiet" key={u.section}>
              <Icon name="info" />
              <div>
                <strong>{u.section}</strong> — {u.reason}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}
